import { blobticService } from './blobticService';

export interface UserNotification {
    NOT_UIDF: number;
    NOT_TITLE: string;
    NOT_MESSAGE: string;
    NOT_DATE: string;
    NOT_IS_READ: boolean;
    NOT_TYPE: 'INFO' | 'WARNING' | 'ERROR' | 'SUCCESS';
    USER_IDF?: number; // Audit
}

export interface ChatMessage {
    CHT_UIDF: number;
    CHT_SENDER_ID: number;
    CHT_RECEIVER_ID: number;
    CHT_MESSAGE: string;
    CHT_DATE: string;
    CHT_IS_READ: boolean;
    SENDER_NAME?: string;
    RECEIVER_NAME?: string;
}

async function executeSilentAction(oKey: string, params: Record<string, unknown> = {}) {
    return blobticService.executeZoomRequest<any[]>({
        ZoomKey: 'CrudOperation',
        Action: { OKey: oKey, ...params },
        useCache: false,
        showLoader: false,
    });
}

export const coreFeatureService = {
    // --- Notifications ---
    async getUserNotifications(): Promise<UserNotification[]> {
        const response = await executeSilentAction('Blobtic_Sys_User,sys_notification,select', {});
        if (response.success && Array.isArray(response.data)) {
            return response.data.map((item: any) => ({
                NOT_UIDF: item.NOT_UIDF,
                NOT_TITLE: item.NOT_TITLE,
                NOT_MESSAGE: item.NOT_MESSAGE,
                NOT_DATE: item.NOT_DATE,
                NOT_IS_READ: Boolean(item.NOT_IS_READ),
                NOT_TYPE: item.NOT_TYPE,
                NOT_USER_IDF: item.NOT_USER_IDF,
                USER_IDF: item.USER_IDF
            }));
        }
        return [];
    },

    async markNotificationRead(notifId: number): Promise<boolean> {
        const response = await executeSilentAction('Blobtic_Sys_User,sys_notification,update', {
            NOT_UIDF: notifId,
            NOT_IS_READ: true
        });
        return response.success;
    },

    async markAllNotificationsRead(userId: number): Promise<boolean> {
        return false;
    },

    async deleteNotification(notifId: number): Promise<boolean> {
        const response = await executeSilentAction('Blobtic_Sys_User,sys_notification,delete', {
            NOT_UIDF: notifId
        });
        return response.success;
    },

    // --- Chat ---
    async getChatUsers(): Promise<any[]> {
        const response = await executeSilentAction('Blobtic_Sys_User,sys_user,select', {
            RETURN_ONOFFLINE: true
        });
        if (response.success && Array.isArray(response.data)) {
            return response.data;
        }
        return [];
    },

    async getChatMessages(otherUserId?: number, page: number = 0, pageSize: number = 20): Promise<ChatMessage[]> {
        const params: Record<string, unknown> = {};
        if (otherUserId) params.OTHER_USER_ID = otherUserId;
        else params.USER_ID = -1;

        if (page !== undefined && pageSize !== undefined) {
            params.PAGE_NUMBER = page;
            params.PAGE_SIZE = pageSize;
            params.OFFSET = page * pageSize;
        }

        const response = await executeSilentAction('Blobtic_Sys_User,sys_chat,select', params);

        if (response.success && Array.isArray(response.data)) {
            return response.data.map((item: any) => ({
                CHT_UIDF: item.CHT_UIDF,
                CHT_SENDER_ID: item.CHT_SENDER_ID,
                CHT_RECEIVER_ID: item.CHT_RECEIVER_ID,
                CHT_MESSAGE: item.CHT_MESSAGE,
                CHT_DATE: item.CHT_DATE,
                CHT_IS_READ: Boolean(item.CHT_IS_READ),
                SENDER_NAME: item.SENDER_NAME,
                RECEIVER_NAME: item.RECEIVER_NAME
            }));
        }
        return [];
    },

    async sendMessage(receiverId: number, message: string): Promise<boolean> {
        const response = await executeSilentAction('Blobtic_Sys_User,sys_chat,insert', {
            CHT_RECEIVER_ID: receiverId,
            CHT_MESSAGE: message
        });
        return response.success;
    },

    async getUnreadChatCount(userId: number): Promise<number> {
        const response = await executeSilentAction('Blobtic_Sys_User,sys_chat,select', {
            UNREAD_FOR_USER: userId,
            RETURN_COUNT: true
        });

        if (response.success && Array.isArray(response.data) && response.data.length > 0) {
            return Number(response.data[0].CNT || 0);
        }
        return 0;
    },

    async getUnreadChatCounts(userId: number): Promise<{ [key: number]: number }> {
        const response = await executeSilentAction('Blobtic_Sys_User,sys_chat,select', {
            UNREAD_FOR_USER: userId,
            RETURN_UNREAD_BY_SENDER: true
        });

        const counts: { [key: number]: number } = {};
        if (response.success && Array.isArray(response.data)) {
            response.data.forEach((item: any) => {
                counts[item.CHT_SENDER_ID] = Number(item.CNT || 0);
            });
        }
        return counts;
    },

    async markChatAsRead(senderId: number, receiverId: number): Promise<boolean> {
        const response = await executeSilentAction('Blobtic_Sys_User,sys_chat,update', {
            MARK_READ_SENDER_ID: senderId,
            MARK_READ_RECEIVER_ID: receiverId,
            CHT_UIDF: -1
        });
        return response.success;
    },

    async callServerAction(oKey: string, params: any = {}): Promise<{ success: boolean; data?: any; message?: string; errorCode?: string }> {
        const response = await executeSilentAction(oKey, params);
        return {
            success: response.success,
            data: response.data,
            message: response.message,
            errorCode: response.code
        };
    }
};
